import React, { useEffect, useMemo, useState } from 'react';
import { ClientConsolidationAPI } from '../services/clientConsolidation';
import { FormaPagamentoAPI } from '../services/formaPagamento';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { ServiceCostCentersAPI } from '../services/serviceCostCenters';
import OperationStatus from './OperationStatus';
import ProposalCostUpdateFlow from './ProposalCostUpdateFlow';
import ProposalFlow from './ProposalFlow';
import QuotationRequestsPanel from './QuotationRequestsPanel';

const PAGE_SIZE = 20;

function formatValue(value, fallback = 'Não informado') {
  return value === null || value === undefined || String(value).trim() === '' ? fallback : String(value);
}

function formatLongValue(value, fallback = 'Sem observação') {
  const text = formatValue(value, fallback);
  return text.length > 180 ? `${text.slice(0, 177)}...` : text;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(value) {
  if (!value) return 'Não informado';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Não informado' : date.toLocaleDateString('pt-BR');
}

function statusLabel(status) {
  return ({
    draft: 'Rascunho',
    pricing: 'Precificando',
    ready_to_quote: 'Pronto para proposta',
    quote_generated: 'Proposta gerada',
    approved: 'Aprovado',
    canceled: 'Cancelado'
  }[status] || formatValue(status, 'Status'));
}

function typeLabel(type) {
  return ({ service: 'Serviço', project: 'Projeto', maintenance: 'Manutenção', installation: 'Instalação' }[type] || formatValue(type, 'Tipo'));
}

function itemCategoryLabel(category) {
  return ({
    refrigeracao: 'Refrigeração',
    eletrica: 'Elétrica',
    gabinete: 'Gabinete',
    compra_local: 'Compra local',
    terceiro: 'Terceiro',
    mao_de_obra: 'Mão de obra',
    outros: 'Outros'
  }[category] || 'Outros');
}

function emptyForm() {
  return {
    title: '',
    description: '',
    type: 'service',
    customerMasterClientId: '',
    blingCustomerId: '',
    marginPercent: 20,
    bdiPercent: 0,
    laborCost: 0,
    travelCost: 0,
    thirdPartyCost: 0,
    extraCosts: 0
  };
}

function purchaseOrderKey(order = {}) {
  return String(order.blingPurchaseOrderId || order.id || order.externalId || '');
}

function getPaymentMethodLabel(method = {}) {
  return formatValue(method.descricao || method.nome || method.name || method.codigo || method.code || method.id, 'Forma sem descrição');
}

function normalizePaymentMethodRows(payload = {}) {
  const candidates = [
    payload.rows,
    payload.data,
    payload.items,
    payload.formasPagamento,
    payload.paymentMethods
  ];
  const rows = candidates.find((candidate) => Array.isArray(candidate)) || [];
  return rows
    .map((row) => {
      const source = row.formaPagamento || row.paymentMethod || row;
      const id = formatValue(source.id || source.codigo || source.code || source.externalId || row.id, '');
      if (!id) return null;
      return {
        ...row,
        id,
        descricao: source.descricao || source.description || row.descricao,
        nome: source.nome || source.name || row.nome,
        codigo: source.codigo || source.code || row.codigo,
        name: source.name || source.nome || source.descricao || source.description || row.name || row.descricao || id
      };
    })
    .filter(Boolean);
}

function isPaymentMethodWarning(message = '') {
  return String(message || '').toLowerCase().includes('forma de pagamento');
}

function getFriendlyErrorMessage(error, fallbackMessage) {
  const message = getErrorMessage(error, fallbackMessage);
  const text = JSON.stringify(error?.response?.data || message).toLowerCase();
  if (
    text.includes('refresh token') ||
    text.includes('invalid_grant') ||
    text.includes('invalid refresh') ||
    text.includes('token invalido') ||
    text.includes('token inválido')
  ) {
    return 'É necessário renovar a autorização do Bling no cec_api_integra para sincronizar formas de pagamento.';
  }
  return message;
}

export default function ServiceCostCentersPanel({ pageMode = false, onClose, onUnauthorized }) {
  const initialId = window.location.pathname.startsWith('/service-cost-centers/')
    ? window.location.pathname.split('/')[2]
    : '';
  const [centersPayload, setCentersPayload] = useState({ rows: [], page: 1, totalPages: 1, total: 0 });
  const [detail, setDetail] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState({ rows: [], collection: '', total: 0 });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');
  const [paymentMethodSearch, setPaymentMethodSearch] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [filters, setFilters] = useState({ q: '', status: '', page: 1 });
  const [orderFilters, setOrderFilters] = useState({ search: '', supplier: '', dateStart: '', dateEnd: '' });
  const [form, setForm] = useState(() => emptyForm());
  const [detailDraft, setDetailDraft] = useState(() => emptyForm());
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState('assistant');
  const [loading, setLoading] = useState(false);
  const [showLoadStatus, setShowLoadStatus] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedOrders = useMemo(
    () => (purchaseOrders.rows || []).filter((order) => selectedOrderIds.includes(purchaseOrderKey(order))),
    [purchaseOrders, selectedOrderIds]
  );

  const handleApiError = (requestError, fallbackMessage) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getFriendlyErrorMessage(requestError, fallbackMessage));
  };

  const loadPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');
    try {
      const payload = await FormaPagamentoAPI.listarFormasPagamento({ search: paymentMethodSearch.trim(), limit: 50 });
      const rows = normalizePaymentMethodRows(payload);
      setPaymentMethods(rows);
      if (rows.length === 1) {
        updateSelectedPaymentMethod(rows[0].id);
        setSuccess(`Forma de pagamento selecionada: ${getPaymentMethodLabel(rows[0])}.`);
      } else if (!rows.length) {
        updateSelectedPaymentMethod('');
        setPaymentMethodsError('Nenhuma forma de pagamento encontrada. Sincronize as formas e selecione uma opção retornada pela busca.');
      }
    } catch (requestError) {
      setPaymentMethods([]);
      setPaymentMethodsError(getFriendlyErrorMessage(requestError, 'Não foi possível carregar as formas de pagamento do Bling.'));
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const previewPaymentMethodId = (payload = preview) => String(payload?.payload?.parcelas?.[0]?.formaPagamento?.id || '');

  const updateSelectedPaymentMethod = (paymentMethodId) => {
    const nextId = String(paymentMethodId || '').trim();
    setSelectedPaymentMethodId(nextId);
    setPreview((current) => {
      if (!current?.payload?.parcelas) return current;
      return {
        ...current,
        payload: {
          ...current.payload,
          parcelas: current.payload.parcelas.map((installment) => ({
            ...installment,
            ...(nextId ? { formaPagamento: { id: nextId } } : { formaPagamento: undefined })
          }))
        }
      };
    });
  };

  const selectPaymentMethod = (method = {}) => {
    updateSelectedPaymentMethod(method.id);
    setPaymentMethodSearch(getPaymentMethodLabel(method));
    setSuccess(`Forma de pagamento selecionada: ${getPaymentMethodLabel(method)} (ID ${method.id}).`);
  };

  const loadCenters = async (page = filters.page) => {
    const payload = await ServiceCostCentersAPI.list({ q: filters.q.trim(), status: filters.status, page, limit: PAGE_SIZE });
    setCentersPayload(payload);
    setFilters((current) => ({ ...current, page }));
  };

  const openDetail = async (id, syncUrl = true) => {
    if (!id) return;
    setActionId('detail');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.detail(id);
      setDetail(payload);
      setDetailDraft({
        title: payload.service?.title || '',
        description: payload.service?.description || '',
        type: payload.service?.type || 'service',
        status: payload.service?.status || 'draft',
        customerMasterClientId: payload.service?.customerMasterClientId?._id || payload.service?.customerMasterClientId || '',
        blingCustomerId: payload.service?.blingCustomerId || '',
        marginPercent: payload.service?.marginPercent || 0,
        bdiPercent: payload.service?.bdiPercent || 0,
        laborCost: payload.service?.laborCost || 0,
        travelCost: payload.service?.travelCost || 0,
        thirdPartyCost: payload.service?.thirdPartyCost || 0,
        extraCosts: payload.service?.extraCosts || 0
      });
      setClientSearch('');
      setClientResults([]);
      setPreview(null);
      setSelectedOrderIds([]);
      if (pageMode) setActiveWorkspaceTab('assistant');
      if (syncUrl) window.history.pushState({}, '', `/service-cost-centers/${id}`);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível abrir o centro de custo.');
    } finally {
      setActionId('');
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setPreview(null);
    setSelectedOrderIds([]);
    window.history.pushState({}, '', '/service-cost-centers');
  };

  const startNewProposal = () => {
    setDetail(null);
    setPreview(null);
    setSelectedOrderIds([]);
    setActiveWorkspaceTab('assistant');
    if (pageMode) window.history.pushState({}, '', '/service-cost-centers');
  };

  const loadAll = async ({ showStatus = false } = {}) => {
    setLoading(true);
    setShowLoadStatus(showStatus);
    setError('');
    try {
      await loadCenters(1);
      if (initialId) await openDetail(initialId, false);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível carregar os centros de custo.');
    } finally {
      setLoading(false);
      setShowLoadStatus(false);
    }
  };

  useEffect(() => {
    if (initialId) void openDetail(initialId, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitFilters = async (event) => {
    event.preventDefault();
    setLoading(true);
    setShowLoadStatus(true);
    setError('');
    try {
      await loadCenters(1);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível aplicar os filtros.');
    } finally {
      setLoading(false);
      setShowLoadStatus(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setActionId('create');
    setError('');
    setSuccess('');
    try {
      const payload = await ServiceCostCentersAPI.create(form);
      setForm(emptyForm());
      setShowCreate(false);
      setSuccess('Centro de custo criado.');
      await loadCenters(1);
      await openDetail(payload.service._id || payload.service.id);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar o centro de custo.');
    } finally {
      setActionId('');
    }
  };

  const searchOrders = async (event) => {
    event?.preventDefault();
    setActionId('orders');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.purchaseOrders({ ...orderFilters, limit: 100 });
      setPurchaseOrders(payload);
      setSelectedOrderIds([]);
      if (!payload.collection) setError('Nenhuma coleção de pedidos de compra do Bling foi encontrada no banco cecbd.');
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível buscar pedidos de compra do Bling.');
    } finally {
      setActionId('');
    }
  };

  const toggleOrder = (order) => {
    const id = purchaseOrderKey(order);
    setSelectedOrderIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const linkSelectedOrders = async () => {
    if (!detail?.service?._id || !selectedOrders.length) return;
    setActionId('link-orders');
    setError('');
    setSuccess('');
    try {
      await ServiceCostCentersAPI.linkPurchaseOrders(detail.service._id, selectedOrders);
      setSuccess('Pedidos de compra vinculados e itens consolidados.');
      await openDetail(detail.service._id, false);
      await loadCenters(filters.page);
      setSelectedOrderIds([]);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível vincular os pedidos selecionados.');
    } finally {
      setActionId('');
    }
  };

  const searchMasterClients = async (event) => {
    event?.preventDefault();
    if (!clientSearch.trim()) return;
    setActionId('client-search');
    setError('');
    try {
      const payload = await ClientConsolidationAPI.search(clientSearch.trim());
      const masters = (payload.masterClients || []).map((client) => ({
        id: client.id || client._id,
        name: client.name || client.corporateName || client.fantasyName,
        documentNumber: client.documentNumber,
        city: client.city,
        state: client.state,
        blingCustomerId: ''
      }));
      const linked = (payload.linkedRecords || [])
        .filter((record) => record.sourceSystem === 'bling' && record.linkedMasterClientId)
        .map((record) => ({
          id: record.linkedMasterClientId,
          name: record.linkedMasterClientName || record.name,
          documentNumber: record.documentNumber,
          city: record.city,
          state: record.state,
          blingCustomerId: record.externalId
        }));
      const seen = new Set();
      const rows = [...linked, ...masters].filter((client) => {
        if (!client.id || seen.has(client.id)) return false;
        seen.add(client.id);
        return true;
      });
      setClientResults(rows);
      if (!rows.length) setError('Nenhum Cliente Mestre encontrado para a busca informada.');
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível buscar Cliente Mestre.');
    } finally {
      setActionId('');
    }
  };

  const selectClientForProposal = (client) => {
    setDetailDraft((current) => ({
      ...current,
      customerMasterClientId: client.id || '',
      blingCustomerId: client.blingCustomerId || current.blingCustomerId || ''
    }));
    setClientSearch(client.name || '');
  };

  const saveProposalData = async () => {
    if (!detail?.service?._id) return;
    setActionId('save-proposal');
    setError('');
    setSuccess('');
    try {
      await ServiceCostCentersAPI.update(detail.service._id, {
        ...detailDraft,
        marginPercent: Number(detailDraft.marginPercent || 0),
        bdiPercent: Number(detailDraft.bdiPercent || 0),
        laborCost: Number(detailDraft.laborCost || 0),
        travelCost: Number(detailDraft.travelCost || 0),
        thirdPartyCost: Number(detailDraft.thirdPartyCost || 0),
        extraCosts: Number(detailDraft.extraCosts || 0)
      });
      setSuccess('Dados da proposta salvos.');
      await openDetail(detail.service._id, false);
      await loadCenters(filters.page);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível salvar os dados da proposta.');
    } finally {
      setActionId('');
    }
  };

  const refreshLinkedOrders = async () => {
    if (!detail?.service?._id) return;
    setActionId('refresh-linked-orders');
    setError('');
    setSuccess('');
    try {
      const payload = await ServiceCostCentersAPI.refreshLinkedPurchaseOrders(detail.service._id);
      setSuccess(`Compras vinculadas atualizadas: ${payload.refreshed || 0}.`);
      await openDetail(detail.service._id, false);
      await loadCenters(filters.page);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível atualizar as compras vinculadas.');
    } finally {
      setActionId('');
    }
  };

  const updateItem = async (item, field, value) => {
    if (!detail?.service?._id) return;
    const numericFields = ['quantity', 'purchaseUnitValue', 'saleUnitValue', 'marginPercent', 'bdiPercent'];
    const payload = { [field]: numericFields.includes(field) ? Number(value || 0) : value };
    if (field === 'includeInProposal') payload[field] = Boolean(value);
    setActionId(`item-${item._id}-${field}`);
    try {
      await ServiceCostCentersAPI.updateItem(detail.service._id, item._id, payload);
      await openDetail(detail.service._id, false);
      await loadCenters(filters.page);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível atualizar o item.');
    } finally {
      setActionId('');
    }
  };

  const recalculate = async () => {
    if (!detail?.service?._id) return;
    setActionId('recalculate');
    try {
      await ServiceCostCentersAPI.recalculate(detail.service._id);
      await openDetail(detail.service._id, false);
      await loadCenters(filters.page);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível recalcular o centro de custo.');
    } finally {
      setActionId('');
    }
  };

  const loadPreview = async () => {
    if (!detail?.service?._id) return;
    setActionId('preview');
    setError('');
    try {
      const payload = await ServiceCostCentersAPI.preview(detail.service._id, selectedPaymentMethodId ? { paymentMethodId: selectedPaymentMethodId } : {});
      setPreview(payload);
      setSelectedPaymentMethodId(previewPaymentMethodId(payload));
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível montar o JSON da proposta comercial.');
    } finally {
      setActionId('');
    }
  };

  const createCommercialProposal = async () => {
    if (!detail?.service?._id) return;
    if (!selectedPaymentMethodId) {
      setError('Selecione ou informe uma forma de pagamento antes de criar a proposta no Bling.');
      return;
    }
    setActionId('create-commercial-proposal');
    setError('');
    try {
      await ServiceCostCentersAPI.createBlingCommercialProposal(detail.service._id, selectedPaymentMethodId ? { paymentMethodId: selectedPaymentMethodId } : {});
      setSuccess('Proposta comercial criada no Bling.');
      await openDetail(detail.service._id, false);
    } catch (requestError) {
      handleApiError(requestError, 'Não foi possível criar a proposta comercial no Bling.');
      if (requestError?.response?.data?.payload) setPreview(requestError.response.data.payload);
    } finally {
      setActionId('');
    }
  };

  const renderPager = () => (
    <div className="client-table-pager">
      <span>{centersPayload.total || 0} centros de custo</span>
      <div className="overlay-actions">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={filters.page <= 1 || loading} onClick={() => loadCenters(filters.page - 1)}>Anterior</button>
        <span>Página {centersPayload.page || 1} de {centersPayload.totalPages || 1}</span>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={filters.page >= centersPayload.totalPages || loading} onClick={() => loadCenters(filters.page + 1)}>Próxima</button>
      </div>
    </div>
  );

  const ShellTag = pageMode ? 'main' : 'section';
  const canCreatePreview = Boolean(
    preview &&
    selectedPaymentMethodId &&
    (preview.canCreate || !(preview.warnings || []).some((warning) => !isPaymentMethodWarning(warning)))
  );
  const operationMessages = {
    detail: ['Abrindo centro de custo.', 'Carregando pedidos vinculados e itens consolidados.'],
    orders: ['Consultando pedidos de compra via cec_api_integra.', 'Aplicando filtros de número, fornecedor e período.'],
    'client-search': ['Buscando Cliente Mestre.', 'Verificando vínculos com cadastro Bling.'],
    'link-orders': ['Vinculando pedidos ao centro de custo.', 'Consolidando itens de compra.'],
    'refresh-linked-orders': ['Atualizando compras vinculadas pelo integrador.'],
    preview: ['Montando payload da proposta.', 'Validando itens e parcelas obrigatórios.'],
    'create-commercial-proposal': ['Enviando proposta para o cec_api_integra.', 'Aguardando criação no Bling.'],
  };

  return (
    <ShellTag className={pageMode ? 'client-consolidation-page' : 'client-consolidation-panel'}>
      <div className="overlay-panel-header service-page-header">
        <div>
          <div className="eyebrow">Propostas</div>
          <h2>Proposta Comercial</h2>
          <p className="text-muted mb-0">Selecione compras, revise custos e crie a proposta no Bling com confirmação.</p>
        </div>
        {!pageMode && <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Voltar</button>}
      </div>

      <OperationStatus
        active={showLoadStatus || Boolean(operationMessages[actionId])}
        title={showLoadStatus ? 'Atualizando centros de custo' : 'Operação em andamento'}
        steps={showLoadStatus ? ['Consultando centros de custo.', 'Atualizando totais e paginação.'] : operationMessages[actionId]}
      />

      {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      {success && <div className="alert alert-success mt-3 mb-0">{success}</div>}
      {showCreate && (
        <form className="client-filter-bar mt-4 align-items-end" onSubmit={handleCreate}>
          <div className="filter-control"><label className="form-label">Título</label><input className="form-control" value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} required placeholder="Ex.: Instalação Geolab" /></div>
          <div className="filter-control"><label className="form-label">Tipo</label><select className="form-select" value={form.type} onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}><option value="service">Serviço</option><option value="project">Projeto</option><option value="maintenance">Manutenção</option><option value="installation">Instalação</option></select></div>
          <div className="filter-control"><label className="form-label">Cliente Mestre ID</label><input className="form-control" value={form.customerMasterClientId} onChange={(event) => setForm((current) => ({ ...current, customerMasterClientId: event.target.value }))} placeholder="Opcional" /></div>
          <div className="filter-control"><label className="form-label">Contato Bling ID</label><input className="form-control" value={form.blingCustomerId} onChange={(event) => setForm((current) => ({ ...current, blingCustomerId: event.target.value }))} placeholder="Opcional" /></div>
          <div className="filter-control"><label className="form-label">Margem %</label><input className="form-control" type="number" value={form.marginPercent} onChange={(event) => setForm((current) => ({ ...current, marginPercent: event.target.value }))} /></div>
          <div className="filter-control"><label className="form-label">BDI %</label><input className="form-control" type="number" value={form.bdiPercent} onChange={(event) => setForm((current) => ({ ...current, bdiPercent: event.target.value }))} /></div>
          <div className="filter-control filter-control--wide"><label className="form-label">Descrição</label><input className="form-control" value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Observação que irá para a prévia" /></div>
          <button type="submit" className="btn btn-dark" disabled={actionId === 'create'}>Criar</button>
        </form>
      )}

      <div className="service-workspace-tabs mt-3" role="tablist" aria-label="Área de propostas">
        <button type="button" className={activeWorkspaceTab === 'assistant' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('assistant')}>
          Assistente de Proposta
        </button>
        <button type="button" className={activeWorkspaceTab === 'proposal-costs' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('proposal-costs')}>
          Custos de propostas Bling
        </button>
        <button type="button" className={activeWorkspaceTab === 'centers' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('centers')}>
          Rascunhos e propostas
        </button>
        <button type="button" className={activeWorkspaceTab === 'quotation-requests' ? 'active' : ''} onClick={() => setActiveWorkspaceTab('quotation-requests')}>Solicitações de cotação</button>
      </div>

      {activeWorkspaceTab === 'assistant' && (
        <section className="client-detail-section service-workspace-panel mt-0">
          <div className="service-workspace-heading">
            <div>
              <div className="eyebrow">{detail?.service?._id ? 'Proposta em andamento' : 'Nova Proposta'}</div>
              <h3>Assistente de Proposta Comercial</h3>
              {detail?.service?._id && (
                <p className="text-muted mb-0">
                  {formatValue(detail.service.title)} · {statusLabel(detail.service.status)} · {formatMoney(detail.service.totalSaleValue)}
                </p>
              )}
            </div>
            <div className="overlay-actions">
              {detail?.service?._id && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={startNewProposal}>Nova proposta</button>}
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading} onClick={() => loadAll({ showStatus: true })}>Atualizar</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setActiveWorkspaceTab('centers'); void loadAll({ showStatus: true }); }}>Ver rascunhos</button>
            </div>
          </div>
          <ProposalFlow
            key={detail?.service?._id || 'new-proposal-flow'}
            initialDetail={detail}
            onUnauthorized={onUnauthorized}
            onPrepared={(payload) => {
              if (payload?.service?._id) {
                setDetail(payload);
                void loadCenters(filters.page);
              }
            }}
            onFinished={() => {
              void loadCenters(filters.page);
            }}
          />
        </section>
      )}

      {activeWorkspaceTab === 'proposal-costs' && (
        <section className="client-detail-section service-workspace-panel mt-0">
          <div className="service-workspace-heading">
            <div>
              <div className="eyebrow">Novo fluxo</div>
              <h3>Cotação e atualização de custos</h3>
              <p className="text-muted mb-0">A proposta nasce no Bling; o TASK consulta cotações, calcula decisões comerciais e solicita a atualização pelo cec_api_integra.</p>
            </div>
          </div>
          <ProposalCostUpdateFlow onUnauthorized={onUnauthorized} />
        </section>
      )}

      {activeWorkspaceTab === 'quotation-requests' && <section className="client-detail-section service-workspace-panel mt-0"><QuotationRequestsPanel onUnauthorized={onUnauthorized} /></section>}

      {activeWorkspaceTab === 'centers' && (
        <section className="service-workspace-panel">
          <div className="service-workspace-heading mt-3">
            <div>
              <div className="eyebrow">Rascunhos e propostas</div>
              <h3>Retomar uma proposta</h3>
            </div>
            <div className="overlay-actions">
              <button type="button" className="btn btn-sm btn-success" onClick={startNewProposal}>Nova proposta</button>
              <button type="button" className="btn btn-sm btn-outline-primary" disabled={loading} onClick={() => loadAll({ showStatus: true })}>Atualizar</button>
            </div>
          </div>
          <form className="client-filter-bar mt-4" onSubmit={handleSubmitFilters}>
            <div className="filter-control"><label className="form-label">Busca</label><input className="form-control" value={filters.q} onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))} placeholder="Título, descrição ou ID Bling" /></div>
            <div className="filter-control"><label className="form-label">Status</label><select className="form-select" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}><option value="">Todos</option><option value="draft">Rascunho</option><option value="pricing">Precificando</option><option value="ready_to_quote">Pronto para proposta</option><option value="quote_generated">Proposta gerada</option></select></div>
            <button type="submit" className="btn btn-dark" disabled={loading}>Aplicar filtros</button>
          </form>

          <div className="client-table-wrap mt-3">
            {renderPager()}
            <table className="client-audit-table">
              <thead><tr><th>Centro de custo</th><th>Cliente</th><th>Tipo</th><th>Status</th><th>Custo</th><th>Venda prevista</th><th>Atualizado</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan="7">Carregando centros de custo...</td></tr>}
                {!loading && !centersPayload.rows?.length && <tr><td colSpan="7">Nenhum centro de custo encontrado.</td></tr>}
                {!loading && centersPayload.rows?.map((center) => (
                  <tr key={center._id} className={detail?.service?._id === center._id ? 'client-row-success' : ''} onClick={() => openDetail(center._id)}>
                    <td><strong>{formatValue(center.title)}</strong><small>{formatValue(center.description, '')}</small></td>
                    <td>{formatValue(center.customerMasterClientId?.name || center.blingCustomerId)}</td>
                    <td>{typeLabel(center.type)}</td>
                    <td>{statusLabel(center.status)}</td>
                    <td>{formatMoney(center.totalCost)}</td>
                    <td>{formatMoney(center.totalSaleValue)}</td>
                    <td>{formatDate(center.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPager()}
          </div>
        </section>
      )}

      {detail && !pageMode && (
        <div className="client-detail-rail">
          <aside className="task-overlay-panel client-detail-drawer shadow-lg">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Centro de custo</div>
                <h2>{formatValue(detail.service?.title)}</h2>
                <p className="text-muted mb-0">{statusLabel(detail.service?.status)} · {formatMoney(detail.service?.totalSaleValue)}</p>
              </div>
              <div className="overlay-actions">
                <button type="button" className="btn btn-primary" disabled={actionId === 'preview'} onClick={loadPreview}>Gerar prévia</button>
                <button type="button" className="btn btn-outline-secondary" onClick={closeDetail}>Fechar</button>
              </div>
            </div>

            <ProposalFlow
              initialDetail={detail}
              onUnauthorized={onUnauthorized}
              onPrepared={(payload) => {
                if (payload?.service?._id) {
                  setDetail(payload);
                  void loadCenters(filters.page);
                }
              }}
              onFinished={() => {
                void loadCenters(filters.page);
              }}
            />

            <details className="proposal-details mt-3">
              <summary>Fluxo antigo e detalhes técnicos</summary>

            <div className="client-detail-section mt-3">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <h3>Dados da proposta</h3>
                <button type="button" className="btn btn-primary" disabled={actionId === 'save-proposal'} onClick={saveProposalData}>
                  {actionId === 'save-proposal' ? 'Salvando...' : 'Salvar dados da proposta'}
                </button>
              </div>
              <div className="client-readonly-grid">
                <div><span>Cliente Mestre</span><strong>{formatValue(detail.service?.customerMasterClientId?.name)}</strong></div>
                <div><span>ID contato Bling</span><strong>{formatValue(detail.service?.blingCustomerId)}</strong></div>
                <div><span>Tipo</span><strong>{typeLabel(detail.service?.type)}</strong></div>
                <div><span>Status</span><strong>{statusLabel(detail.service?.status)}</strong></div>
                <div><span>Custo compras</span><strong>{formatMoney(detail.service?.totalPurchaseCost)}</strong></div>
                <div><span>Custos internos</span><strong>{formatMoney(detail.service?.totalInternalCost)}</strong></div>
                <div><span>Custo total</span><strong>{formatMoney(detail.service?.totalCost)}</strong></div>
                <div><span>Venda prevista</span><strong>{formatMoney(detail.service?.totalSaleValue)}</strong></div>
              </div>
              <div className="client-filter-bar mt-3">
                <div className="filter-control filter-control--wide"><label className="form-label">Título</label><input className="form-control" value={detailDraft.title} onChange={(event) => setDetailDraft((current) => ({ ...current, title: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Tipo</label><select className="form-select" value={detailDraft.type} onChange={(event) => setDetailDraft((current) => ({ ...current, type: event.target.value }))}><option value="service">Serviço</option><option value="project">Projeto</option><option value="maintenance">Manutenção</option><option value="installation">Instalação</option></select></div>
                <div className="filter-control"><label className="form-label">Status</label><select className="form-select" value={detailDraft.status} onChange={(event) => setDetailDraft((current) => ({ ...current, status: event.target.value }))}><option value="draft">Rascunho</option><option value="pricing">Precificando</option><option value="ready_to_quote">Pronto para proposta</option><option value="approved">Aprovado</option><option value="canceled">Cancelado</option></select></div>
                <div className="filter-control filter-control--wide"><label className="form-label">Observações da proposta</label><input className="form-control" value={detailDraft.description} onChange={(event) => setDetailDraft((current) => ({ ...current, description: event.target.value }))} placeholder="Texto que irá para a prévia enviada ao Bling" /></div>
              </div>
              <form className="client-filter-bar mt-3" onSubmit={searchMasterClients}>
                <div className="filter-control filter-control--wide"><label className="form-label">Buscar Cliente Mestre</label><input className="form-control" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder="Nome, CNPJ, telefone ou e-mail" /></div>
                <button type="submit" className="btn btn-dark" disabled={actionId === 'client-search'}>Buscar cliente</button>
              </form>
              {!!clientResults.length && (
                <div className="client-linked-list mt-2">
                  {clientResults.map((client) => (
                    <button type="button" key={client.id} className="client-existing-link" onClick={() => selectClientForProposal(client)}>
                      <strong>{formatValue(client.name)}</strong>
                      <span>{formatValue(client.documentNumber, 'Sem CNPJ')} · {formatValue([client.city, client.state].filter(Boolean).join('/'), 'Cidade não informada')} · Bling {formatValue(client.blingCustomerId)}</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="client-filter-bar mt-3">
                <div className="filter-control"><label className="form-label">Cliente Mestre ID</label><input className="form-control" value={detailDraft.customerMasterClientId} onChange={(event) => setDetailDraft((current) => ({ ...current, customerMasterClientId: event.target.value }))} placeholder="Selecione pela busca ou informe o ID" /></div>
                <div className="filter-control"><label className="form-label">Contato Bling ID</label><input className="form-control" value={detailDraft.blingCustomerId} onChange={(event) => setDetailDraft((current) => ({ ...current, blingCustomerId: event.target.value }))} placeholder="ID do contato aceito nas propostas do Bling" /></div>
                <div className="filter-control"><label className="form-label">Margem %</label><input className="form-control" type="number" value={detailDraft.marginPercent} onChange={(event) => setDetailDraft((current) => ({ ...current, marginPercent: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">BDI %</label><input className="form-control" type="number" value={detailDraft.bdiPercent} onChange={(event) => setDetailDraft((current) => ({ ...current, bdiPercent: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Mão de obra</label><input className="form-control" type="number" value={detailDraft.laborCost} onChange={(event) => setDetailDraft((current) => ({ ...current, laborCost: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Deslocamento</label><input className="form-control" type="number" value={detailDraft.travelCost} onChange={(event) => setDetailDraft((current) => ({ ...current, travelCost: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Terceiros</label><input className="form-control" type="number" value={detailDraft.thirdPartyCost} onChange={(event) => setDetailDraft((current) => ({ ...current, thirdPartyCost: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Custos extras</label><input className="form-control" type="number" value={detailDraft.extraCosts} onChange={(event) => setDetailDraft((current) => ({ ...current, extraCosts: event.target.value }))} /></div>
                <button type="button" className="btn btn-outline-primary" disabled={actionId === 'recalculate'} onClick={recalculate}>Recalcular</button>
              </div>
            </div>

            <div className="client-detail-section mt-3">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <h3>Pedidos de compra do Bling</h3>
                <div className="overlay-actions">
                  <button type="button" className="btn btn-outline-primary" disabled={!detail.purchaseOrders?.length || actionId === 'refresh-linked-orders'} onClick={refreshLinkedOrders}>Atualizar compras vinculadas</button>
                  <button type="button" className="btn btn-primary" disabled={!selectedOrders.length || actionId === 'link-orders'} onClick={linkSelectedOrders}>Vincular pedidos selecionados</button>
                </div>
              </div>
              <form className="client-filter-bar mt-3" onSubmit={searchOrders}>
                <div className="filter-control"><label className="form-label">Busca</label><input className="form-control" value={orderFilters.search} onChange={(event) => setOrderFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Número ou fornecedor" /></div>
                <div className="filter-control"><label className="form-label">Fornecedor</label><input className="form-control" value={orderFilters.supplier} onChange={(event) => setOrderFilters((current) => ({ ...current, supplier: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">De</label><input className="form-control" type="date" value={orderFilters.dateStart} onChange={(event) => setOrderFilters((current) => ({ ...current, dateStart: event.target.value }))} /></div>
                <div className="filter-control"><label className="form-label">Até</label><input className="form-control" type="date" value={orderFilters.dateEnd} onChange={(event) => setOrderFilters((current) => ({ ...current, dateEnd: event.target.value }))} /></div>
                <button type="submit" className="btn btn-dark" disabled={actionId === 'orders'}>Buscar compras</button>
              </form>
              <div className="client-table-wrap mt-3">
                <table className="client-audit-table">
                  <thead><tr><th></th><th>Pedido</th><th>Fornecedor</th><th>Observação interna</th><th>Emissão</th><th>Status</th><th>Total</th></tr></thead>
                  <tbody>
                    {!purchaseOrders.rows?.length && <tr><td colSpan="7">Busque pedidos de compra para vincular a este centro de custo.</td></tr>}
                    {purchaseOrders.rows?.map((order) => (
                      <tr key={order.blingPurchaseOrderId}>
                        <td><input type="checkbox" checked={selectedOrderIds.includes(order.blingPurchaseOrderId)} onChange={() => toggleOrder(order)} /></td>
                        <td>{formatValue(order.blingPurchaseOrderNumber)}</td>
                        <td>{formatValue(order.supplierName)}</td>
                        <td className="purchase-order-notes" title={formatValue(order.internalNotes, '')}>{formatLongValue(order.internalNotes)}</td>
                        <td>{formatDate(order.issueDate)}</td>
                        <td>{formatValue(order.status)}</td>
                        <td>{formatMoney(order.totalValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="client-detail-section mt-3">
              <h3>Pedidos vinculados</h3>
              <div className="client-linked-list">
                {!detail.purchaseOrders?.length && <div className="empty-state">Nenhum pedido de compra vinculado.</div>}
                {detail.purchaseOrders?.map((order) => <article key={order._id} className="client-linked-card"><div><strong>Pedido {formatValue(order.blingPurchaseOrderNumber)}</strong><span>{formatValue(order.supplierName)} · {formatDate(order.issueDate)} · {formatMoney(order.totalValue)}</span></div></article>)}
              </div>
            </div>

            <div className="client-detail-section mt-3">
              <h3>Itens consolidados</h3>
              <div className="client-table-wrap">
                <table className="client-audit-table">
                  <thead><tr><th>Entra</th><th>Descrição</th><th>Categoria</th><th>Qtd.</th><th>Custo un.</th><th>Venda un.</th><th>Total venda</th><th>Produto Mestre</th></tr></thead>
                  <tbody>
                    {!detail.items?.length && <tr><td colSpan="8">Nenhum item consolidado ainda.</td></tr>}
                    {detail.items?.map((item) => (
                      <tr key={item._id}>
                        <td><input type="checkbox" checked={Boolean(item.includeInProposal)} onChange={(event) => updateItem(item, 'includeInProposal', event.target.checked)} /></td>
                        <td><input className="form-control form-control-sm" defaultValue={item.proposalDescription || item.description} onBlur={(event) => updateItem(item, 'proposalDescription', event.target.value)} /></td>
                        <td><select className="form-select form-select-sm" defaultValue={item.category || 'outros'} onChange={(event) => updateItem(item, 'category', event.target.value)}><option value="refrigeracao">Refrigeração</option><option value="eletrica">Elétrica</option><option value="gabinete">Gabinete</option><option value="compra_local">Compra local</option><option value="terceiro">Terceiro</option><option value="mao_de_obra">Mão de obra</option><option value="outros">Outros</option></select><small>{itemCategoryLabel(item.category)}</small></td>
                        <td><input className="form-control form-control-sm" type="number" defaultValue={item.quantity || 0} onBlur={(event) => updateItem(item, 'quantity', event.target.value)} /></td>
                        <td><input className="form-control form-control-sm" type="number" defaultValue={item.purchaseUnitValue || 0} onBlur={(event) => updateItem(item, 'purchaseUnitValue', event.target.value)} /></td>
                        <td><input className="form-control form-control-sm" type="number" defaultValue={item.saleUnitValue || 0} onBlur={(event) => updateItem(item, 'saleUnitValue', event.target.value)} /></td>
                        <td>{formatMoney(item.saleTotalValue)}</td>
                        <td>{formatValue(item.productMasterId?.name, 'Sem vínculo')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {preview && (
              <div className="client-detail-section mt-3">
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <h3>JSON da Proposta Comercial</h3>
                  <button type="button" className="btn btn-success" disabled={!canCreatePreview || actionId === 'create-commercial-proposal'} onClick={createCommercialProposal}>Criar proposta no Bling</button>
                </div>
                {!!preview.warnings?.length && <div className="alert alert-warning">{preview.warnings.join(' ')}</div>}
                <div className="client-readonly-grid">
                  <div><span>Contato Bling</span><strong>{formatValue(preview.payload?.cliente?.blingContatoId)}</strong></div>
                  <div><span>Total</span><strong>{formatMoney(preview.total)}</strong></div>
                  <div><span>Itens</span><strong>{preview.payload?.itens?.length || 0}</strong></div>
                </div>
                <div className="client-filter-bar mt-3">
                  <div className="filter-control filter-control--wide">
                    <label className="form-label">Forma de pagamento</label>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        className="form-control"
                        value={paymentMethodSearch}
                        onChange={(event) => setPaymentMethodSearch(event.target.value)}
                        placeholder="Buscar por descrição, nome ou código"
                      />
                      <button type="button" className="btn btn-outline-secondary" disabled={paymentMethodsLoading} onClick={loadPaymentMethods}>
                        Buscar
                      </button>
                    </div>
                    {!!paymentMethods.length && (
                      <div className="client-linked-list mb-2">
                        {paymentMethods.map((method) => (
                          <button
                            type="button"
                            key={method.id}
                            className={`client-existing-link ${String(method.id) === String(selectedPaymentMethodId) ? 'active' : ''}`}
                            onClick={() => selectPaymentMethod(method)}
                          >
                            <strong>{getPaymentMethodLabel(method)}</strong>
                            <span>ID {method.id}{method.codigo ? ` · Código ${method.codigo}` : ''}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <input
                      className="form-control"
                      value={selectedPaymentMethodId}
                      readOnly
                      placeholder="ID selecionado pela busca"
                    />
                    <div className="small text-muted mt-2">
                      {paymentMethodsLoading
                        ? 'Carregando formas cadastradas no Bling.'
                        : paymentMethodsError || 'Selecione a forma que será enviada nas parcelas desta proposta.'}
                    </div>
                  </div>
                </div>
                <pre className="client-json-preview">{JSON.stringify(preview.payload, null, 2)}</pre>
                {detail.service?.metadata?.cecApiIntegra?.lastProposalAttempt && (
                  <div className="mt-3">
                    <h3>Última tentativa de integração</h3>
                    <pre className="client-json-preview">
                      {JSON.stringify(detail.service.metadata.cecApiIntegra.lastProposalAttempt, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            </details>
          </aside>
        </div>
      )}
    </ShellTag>
  );
}
